import { useMemo } from 'react';
import { useGetWorkspaceMembers } from '@cbhq/identity-api-hooks/account-management/fetch';

import { useIsUserProfileLoading, useUserProfile } from '../../../contexts/UserProfileContext';
import type { WorkspaceTeam, WorkspaceTeamMember } from '../utils/workspaceTeam';
import { buildWorkspaceTeam } from '../utils/workspaceTeam';

export type UseWorkspaceTeamResult = WorkspaceTeam & {
  /** The signed-in user's entry, matched by the GET /v1/user UUID. */
  currentMember: WorkspaceTeamMember | undefined;
  /** True once GET /organization/v1/workspace/members responds, whatever GET /v1/user does. */
  hasResponse: boolean;
  /**
   * False until both GET /organization/v1/workspace/members and GET /v1/user
   * respond, so `currentMember` is final once this is true.
   */
  isLoaded: boolean;
};

/**
 * Fetches the organization's workspace members and normalizes them once per
 * response, so `members` and the maps change identity only when the response
 * does. An empty `organizationId` skips the request.
 */
export function useWorkspaceTeam(organizationId: string): UseWorkspaceTeamResult {
  const response = useGetWorkspaceMembers(
    { organizationId },
    { skip: organizationId.length === 0 },
  );
  const currentUserUuid = useUserProfile()?.id;
  const isUserProfileLoading = useIsUserProfileLoading();

  const team = useMemo(() => buildWorkspaceTeam(response), [response]);

  return useMemo(
    () => ({
      ...team,
      currentMember: currentUserUuid ? team.membersById.get(currentUserUuid) : undefined,
      hasResponse: response != null,
      isLoaded: response != null && !isUserProfileLoading,
    }),
    [team, response, currentUserUuid, isUserProfileLoading],
  );
}
